export type SamplePrompt = {
  n: number;
  title: string;
  text: string;
  tips: string[];
};

export const aiTargets = [
  { name: 'ChatGPT', url: (q: string) => `https://chatgpt.com/?q=${encodeURIComponent(q)}` },
  { name: 'Claude', url: (q: string) => `https://claude.ai/new?q=${encodeURIComponent(q)}` },
  { name: 'Gemini', url: () => 'https://gemini.google.com/app' },
  { name: 'DeepSeek', url: () => 'https://chat.deepseek.com/' },
];

export const promptAnatomy = ['What role to play', 'What information to analyze', 'What to create', 'How to structure the result'];

export const demoInput = `Act as an expert book development strategist specializing in [BOOK GENRE] and help me transform my raw book concept into a professional book blueprint.

Analyze the following information:
[BOOK IDEA]
[TARGET READER]
[MAIN THEME]
[DESIRED TONE]

Create a structured roadmap including the central premise, reader promise, major sections or chapters, key conflicts, emotional progression and the logical sequence of the book.`;

export const demoOutput = [
  { title: 'Core Premise', desc: 'A clear description of the central concept.' },
  { title: 'Target Reader', desc: 'The specific reader the book is designed to serve.' },
  { title: 'Central Theme', desc: 'The primary idea or transformation explored throughout the book.' },
  { title: 'Book Structure', desc: 'A logical sequence of chapters and major sections.' },
  { title: 'Reader Journey', desc: 'How the reader progresses from the beginning of the book to the desired outcome.' },
  { title: 'Next Development Steps', desc: 'The recommended next actions for continuing the manuscript.' },
];

const startTips = ['Define the reader.', 'Define the transformation.', 'Define the central promise of the book.'];

export const samplePrompts: SamplePrompt[] = [
  {
    n: 1,
    title: 'Book Blueprint',
    text: `Act as an expert book development strategist specializing in [BOOK GENRE].

Your goal is to transform my raw concept into a professional book blueprint.

Analyze the [BOOK IDEA], identify the most important reader needs and establish the central premise, core theme ([CENTRAL THEME]) and intended reader experience.

Define the [TARGET READER] and explain what this reader should understand, experience or accomplish by reaching the end of the book.

Develop a logical structure for the manuscript, including major sections, chapter objectives and progression, keeping a [DESIRED TONE] tone and a total length of approximately [BOOK LENGTH].

Identify the central conflict or problem, supporting themes and important narrative or educational elements.

Ensure every chapter contributes to the overall purpose of the book and that the progression feels intentional rather than repetitive.

Finally, create a practical roadmap showing what should be developed next.

If key information is missing from the bracketed fields, ask me the necessary questions before responding.`,
    tips: startTips,
  },
  {
    n: 2,
    title: 'Character Development',
    text: `Act as an expert character development coach specializing in [BOOK GENRE].

Your goal is to help me build a deep, consistent and believable character.

Analyze [CHARACTER NAME] and their role in the story: [CHARACTER ROLE].

Define their core psychology, including their [MAIN GOAL], their deepest [CORE FEAR] and the internal conflict that holds them back.

Develop a meaningful backstory that explains why they think and act the way they do, without contradicting the established [STORY CONTEXT].

Map their character arc from the beginning of the book to the end, showing how each major event changes them.

Describe their voice: how they speak, what they avoid saying and how other characters perceive them.

Finally, list three scenes where this character's motivations should become visible to the reader.

If key information is missing from the bracketed fields, ask me the necessary questions before responding.`,
    tips: startTips,
  },
  {
    n: 3,
    title: 'Chapter Builder',
    text: `Act as an expert manuscript development editor specializing in [BOOK GENRE].

Your goal is to turn my chapter outline into a clear, scene-by-scene writing plan.

Analyze the chapter summary: [CHAPTER SUMMARY].

Identify the chapter objective and how it moves the story toward [STORY GOAL].

Break the chapter into scenes. For each scene, define the point-of-view character ([POV CHARACTER]), the scene objective, the conflict, the stakes and the emotional shift.

Suggest a strong chapter opening and a chapter ending that creates momentum toward the next chapter: [NEXT CHAPTER].

Keep the pacing consistent with the [DESIRED TONE] of the book.

Finally, give me the first concrete writing step so I know exactly what to write next.

If key information is missing from the bracketed fields, ask me the necessary questions before responding.`,
    tips: startTips,
  },
];
